import { useTranslation } from '@/composables/useTranslation'
import { Country } from '@/models/Global'
import Message from '@/models/Message'
import { SimplifiedProduct } from '@/models/Product'
import { SimplifiedCollection } from '@/models/Taxonomy'
import DataService from '@/services/DataService'
import { Debouncer } from '@/utils/Debouncer'
import { arrayToRecord } from '@/utils/tools'
import { AxiosError } from 'axios'
import { defineStore } from 'pinia'
import { computed, nextTick, onMounted, ref, watch } from 'vue'
import { useRoute } from 'vue-router'
import { useDefaultStore } from '.'
import { usePropertyStore } from './propertyStore'
import { useTaxonomiesStore } from './taxonomiesStore'

export const useProductsStore = defineStore('products-archive-store', () => {
  let currentProductsFullPath = ''

  // -----------
  // -- Setup --
  // -----------
  const store = useDefaultStore()
  const { incPropertyResetKey } = usePropertyStore()
  const taxonomiesStore = useTaxonomiesStore()

  const route = useRoute()
  const { appTranslate } = useTranslation()

  const fetchDebouncer = new Debouncer()

  // -----------------------
  // -- Reactives (state) --
  // -----------------------
  const currentProducts = ref<Array<SimplifiedProduct | undefined>>([]) // products for latest response

  const currentTotalProducts = ref(0) // total of latest response
  const currentTotalProductsByCountry = ref<Partial<Record<Country | 'missing', number>>>()
  const loading = ref(new Set<'fetch' | 'startingPoints' | 'restore'>([]))
  const currentFetchErrored = ref(false)

  // const allProductProperties = ref<EditableAdvancedProperty[]>()

  const collectionsInActiveFilter = ref<SimplifiedCollection[]>()
  const expiredProducts = ref<Array<SimplifiedProduct>>() // products related to collection(s) in active filter (if any)

  const showPriceAdjustmentHandler = ref(false)

  // ------------------------------
  // -- Computed props (getters) --
  // ------------------------------

  const watchedRoute = computed(() => ({
    name: route.name,
    params: route.params,
    query: route.query,
    fullPath: route.fullPath,
  }))

  const allProductsLoaded = computed(() => {
    const loadedProducts = currentProducts.value?.reduce((sum, product) => (sum += product ? 1 : 0), 0)
    return currentTotalProducts.value === loadedProducts && !loading.value.size && !currentFetchErrored.value
  })

  // --------------
  // -- Watchers --
  // --------------

  // let previousRoute = { ...watchedRoute.value }

  watch(
    watchedRoute,
    (to, from) => {
      // previousRoute = { ...from }

      if (to.name === 'products') {
        if (currentProductsFullPath !== to.fullPath) {
          // introducing a debounce effect before products are fetched to prevent fetching multiple times when adding/removing filters very fast
          if (from.name === 'products') {
            fetchDebouncer.debounce(async () => {
              history.state.forceUpdate = false
              fetchProducts(true)
            }, 500)
          } else {
            history.state.forceUpdate = false
            fetchProducts(true)
          }
        } else if (from.name === 'product') {
          refreshProduct(from.params.sku as string)
        }

        // flush expired products and update expired collections if collection filter changes
        if (to.query._collections !== from.query._collections) {
          updateExpiredCollectionsData()
          expiredProducts.value = undefined
        }
      }
    },
    { deep: true },
  )

  watch(
    [
      () => store.dataCulture,
      () => store.translationLanguage,
      () => store.productType,
      () => store.explicitlySelectedCountries,
      () => store.explicityIncludeProductsMissingAvailableCountries,
    ],
    () => {
      if (route.name === 'products' && store.isSignedIn) {
        fetchDebouncer.debounce(async () => {
          if (!currentProducts.value.length) {
            fetchProducts(true)
          } else {
            // make sure to trigger intersection observers
            currentProductsFullPath = ''
            currentProducts.value = currentProducts.value.fill(undefined) // fills with empties and then lets intersection observer trigger fetch for those visible
            await nextTick()
            // fetchProducts() // can help trigger a fetch when it gets stuck. --disabled 250909-causes extra fetches
          }
          expiredProducts.value = undefined
        }, store.cultureChangeFetchDelay)
      } else {
        // invalidate currentProducts, but dont fetch when not in the products archive view
        currentProductsFullPath = ''
        currentProducts.value = currentProducts.value.fill(undefined) // fills with empties and then lets intersection observer trigger fetch for those visible
      }
    },
    { deep: true },
  )

  // ---------------------
  // -- Lifecycle hooks --
  // ---------------------
  onMounted(() => {
    updateExpiredCollectionsData()
  })

  // -------------------------
  // -- Functions (actions) --
  // -------------------------

  // const fetchLimit = computed(() => (productsViewStore.view === 'default' ? 10 : 20))
  const fetchLimit = computed(() => 10)

  const maybeClearProducts = (forceClear?: boolean) => {
    if (forceClear || currentProductsFullPath !== route.fullPath) {
      currentProducts.value = []
      currentProductsFullPath = ''
      // currentTotalProducts.value = 0
      currentTotalProductsByCountry.value = undefined
      expiredProducts.value = undefined

      taxonomiesStore.getTaxonomicData('attributes') // trigger fetch if data is stale
      return true
    }
  }

  const fetchProducts = async (clear?: boolean, requestIndex?: number) => {
    if (loading.value.has('fetch')) {
      return
    }

    let cleared = maybeClearProducts(clear)

    const from = requestIndex !== undefined ? Math.floor(requestIndex / fetchLimit.value) * fetchLimit.value : currentProducts.value.length
    const to = from + fetchLimit.value

    if (from > 0 && from >= currentTotalProducts.value && !cleared) {
      return
    }

    try {
      loading.value.add('fetch')

      currentProductsFullPath = route.fullPath

      const response = await DataService.getProducts(
        {
          from,
          to,
          ...route.query,
        },
        {
          includeCultureExclusiveData: true,
        },
        {
          doNotUseStandardOptionsAsFallback: true,
        },
      )

      // check again to make sure the received response is the latest request from the user (not sure if necessary anymore)
      if (currentProductsFullPath === route.fullPath) {
        // fix for products sometimes displaying twice
        cleared = maybeClearProducts(clear) || cleared

        currentTotalProducts.value = response.total
        currentFetchErrored.value = !!response.errored

        for (let i = 0; i < response.results; i++) {
          const index = from + i
          currentProducts.value[index] = response.data[i]
        }

        if (currentProducts.value.length > currentTotalProducts.value) {
          // limit currentProducts to current total. can happen when currentProducts is filled with undefined (after a culture change)
          currentProducts.value.length = currentTotalProducts.value
        }

        if (cleared) {
          DataService.getProductTotalsByCountry(route.query).then((result) => {
            currentTotalProductsByCountry.value = result
          })
        }

        return true
      }
    } catch (err: any) {
      console.log(err)
      store.addMessage(new Message(appTranslate('An error has occured'), (err as AxiosError).response?.data || err.message || err, 'danger'))
    } finally {
      loading.value.delete('fetch')
    }
  }

  const refreshProduct = async (sku: string) => {
    if (currentProducts.value?.length && sku) {
      const indexOfProduct = currentProducts.value?.findIndex((p: SimplifiedProduct | undefined) => p?.sku === sku)

      if (indexOfProduct >= 0) {
        const product = await DataService.getProduct(sku, true, { includeCultureExclusiveData: true })
        if (product) {
          currentProducts.value[indexOfProduct] = product
          // console.log('refreshing', sku)
          incPropertyResetKey(sku)
        } else {
          // product was deleted
          currentProducts.value.splice(indexOfProduct, 1)
        }
      } else {
        // product not found
        // probably changed SKU and was not found in currentProducts
        if (!expiredProducts.value?.some((p) => p?.sku === sku)) {
          // wasn't found in expiredProducts either
          // filling with empty doesn't reset scroll unlike a fresh fetch
          currentProducts.value = currentProducts.value.fill(undefined)
        }
      }
    }
  }

  /**
   * Fetches products
   * Used by intersection observers
   * @param requestIndex Requested product row
   * @param debounceTimeMs delay
   */
  const loadProducts = async (requestIndex?: number, debounceTimeMs?: number) => {
    if (debounceTimeMs) {
      return new Promise((resolve) => {
        fetchDebouncer.debounce(async () => {
          const fetched = await fetchProducts(false, requestIndex)
          resolve(fetched)
        }, debounceTimeMs)
      })
    } else {
      return fetchProducts(false, requestIndex)
    }
  }

  // update expired collections data related to a collection in the active filter (need name and expiredRelations from filter collection id(s))
  const updateExpiredCollectionsData = async () => {
    const collectionIdsInActiveFilter = route.query._collections as string | string[]

    if (collectionIdsInActiveFilter) {
      const collections = await taxonomiesStore.getTaxonomicData('collections')
      const collectionsById = arrayToRecord(collections || [], 'id')

      const collectionsArray = Array.isArray(collectionIdsInActiveFilter) ? collectionIdsInActiveFilter : collectionIdsInActiveFilter.split(',')
      collectionsInActiveFilter.value = collectionsArray.reduce((acc: SimplifiedCollection[], collectionId: string) => {
        const found = collectionsById[collectionId]
        found && acc.push(found)
        return acc
      }, [])
    } else {
      collectionsInActiveFilter.value = undefined
    }
  }

  const fetchExpiredProducts = async () => {
    if (relatedCollectionIds.value?.length) {
      const response = await DataService.getProducts({ _collections: relatedCollectionIds.value?.join(','), includeCultureExclusiveData: 1 })
      expiredProducts.value = response.data
    }
  }

  const collectionsWithRelations = computed(() => collectionsInActiveFilter.value?.filter((collection) => collection.relations?.length))
  const relatedCollectionIds = computed(() => collectionsWithRelations.value?.flatMap((collection) => collection.relations))

  return {
    // reactive vars
    loading,
    currentProducts, // used by all archive views
    currentTotalProducts,
    currentTotalProductsByCountry,
    allProductsLoaded,
    expiredProducts,
    collectionsInActiveFilter,
    relatedCollectionIds,
    collectionsWithRelations,
    showPriceAdjustmentHandler,

    // computed
    fetchLimit,

    // functions
    fetchProducts,
    loadProducts,
    fetchExpiredProducts,
  }
})
